// ✅ src/pages/VehicleV2/LubricationRequest.jsx
// UPDATED FULL FILE — production listing design template
//
// Logic kept:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + initLoading + itemMasterLoading + mainLoading + submitLoading
// - stable init / top search / filtration / add / submit flow
// - avoids repeated API calls
// - itemCategoryId locked to 37 (ASPX)
// - filtration search sends exact Select values
//
// FLOW (ASPX-matched)
// - On load: init + auto top search like FillDetails()
// - Top Search: if already created -> load existing rows
// - Listing Details: incoming requestId loads existing rows
// - Filtration Search -> item master + Add
// - Add -> item/detail for selected items -> main grid
// - Submit -> save master + detail -> listing page

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSearch,
  FaPlus,
  FaSave,
  FaTrash,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
  FaEye,
  FaTimes,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/lubricationRequest";

/* ---------------- helpers ---------------- */
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const uniqBy = (arr, keyFn) => {
  const seen = new Set();
  return arr.filter((x) => {
    const k = keyFn(x);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const normalizeOptionList = (arr, fallbackLabel = "Select") => {
  const list = toList(arr).map((x) => ({
    label: toStr(x.label ?? x.name ?? x.text ?? fallbackLabel, fallbackLabel),
    value: String(x.value ?? x.id ?? x.key ?? fallbackLabel),
  }));

  return list.length ? list : [{ value: fallbackLabel, label: fallbackLabel }];
};

const ensureLubricationCategoryOptions = (arr) => {
  const list = normalizeOptionList(arr, "Select");
  if (!list.some((x) => String(x.value) === "37")) {
    list.push({ value: "37", label: "Lubrication" });
  }
  return list;
};

const normalizeYearList = (arr) => {
  const list = toList(arr).map((x) => String(x));
  return list.length ? list : ["Select"];
};

const normalizeMainRow = (r) => {
  const itemId =
    r.fld_itemid ?? r.itemid ?? r.ItemId ?? r.fld_itemId ?? r.fld_itemID;
  const uomId =
    r.fld_uomid ?? r.uomid ?? r.UomId ?? r.fld_uomId ?? r.fld_uomID;

  const closingsock = toNum(
    r.closingsock ??
      r.closingstock ??
      r.totalconsumptionpreviousmont ??
      r.totalconsumptionpreviousmonth ??
      r.fld_closing_stock
  );

  const curReq = toNum(
    r.Currentmonthrequirement ??
      r.currentmonthrequirement ??
      r.currentRequirement
  );

  const finalReqRaw =
    r.Currentmonthfinalrequirement ??
    r.currentmonthfinalrequirement ??
    r.finalRequirement;

  const finalReq = Number.isFinite(Number(finalReqRaw))
    ? toNum(finalReqRaw)
    : Math.max(0, closingsock - curReq);

  return {
    fld_itemname:
      r.fld_itemname ?? r.itemname ?? r.ItemName ?? r.itemname ?? "",
    fld_itemid: toInt(itemId),
    fld_uomname: r.fld_uomname ?? r.uomname ?? r.itemuom ?? r.UOM ?? "",
    fld_uomid: toInt(uomId),

    openingqty: toNum(r.openingqty ?? r.openingQty ?? 0),
    received: toNum(r.received ?? 0),
    totalpreviousmonth: toNum(r.totalpreviousmonth ?? 0),
    consumptionpreviousmonth: toNum(r.consumptionpreviousmonth ?? 0),
    closingsock,
    totalconsumptionpreviousmont: toNum(
      r.totalconsumptionpreviousmont ??
        r.totalconsumptionpreviousmonth ??
        closingsock
    ),

    Currentmonthrequirement: curReq,
    Currentmonthfinalrequirement: finalReq,
    Remarks: toStr(r.Remarks ?? r.remarks ?? ""),
  };
};

const calcFinal = (row) =>
  Math.max(0, toNum(row.closingsock) - toNum(row.Currentmonthrequirement));

const extractItemIdFromMasterRow = (r) =>
  r.itemid ?? r.fld_itemid ?? r.itemId ?? r.ItemId ?? r.itemcode ?? r.ItemCode;

const extractDisplayFromMasterRow = (r) => ({
  itemCode: r.itemcode ?? r.fld_itemcode ?? r.ItemCode ?? "",
  itemName: r.itemname ?? r.fld_itemname ?? r.ItemName ?? "",
  partNo: r.itemPartNo ?? r.partno ?? r.fld_partno ?? "",
  uom: r.itemuom ?? r.uomname ?? r.fld_uomname ?? "",
});

export default function LubricationRequest() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, branchType, userId]
  );

  /* ===========================
     nav state
  =========================== */
  const navState = location.state || {};
  const incomingRequestId = navState?.requestId || navState?.id || "";
  const incomingRow = navState?.row || null;

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [itemMasterLoading, setItemMasterLoading] = useState(false);
  const [mainLoading, setMainLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const isBusy =
    pageLoading ||
    initLoading ||
    itemMasterLoading ||
    mainLoading ||
    submitLoading;

  /* ===========================
     init dropdowns
  =========================== */
  const [branches, setBranches] = useState([
    { value: "Select", label: "Select" },
  ]);
  const [years, setYears] = useState(["Select"]);
  const [months, setMonths] = useState([{ value: "Select", label: "Select" }]);
  const [itemCategories, setItemCategories] = useState([
    { value: "Select", label: "Select" },
  ]);
  const [itemNames, setItemNames] = useState([
    { value: "Select", label: "Select" },
  ]);
  const [itemCodes, setItemCodes] = useState([
    { value: "Select", label: "Select" },
  ]);

  /* ===========================
     top filters
  =========================== */
  const [top, setTop] = useState({
    branchId: String(sessionBranchId),
    year: "Select",
    month: "Select",
  });

  /* ===========================
     filtration
  =========================== */
  const [fil, setFil] = useState({
    itemCategoryId: "37",
    itemName: "Select",
    itemCode: "Select",
  });

  /* ===========================
     ui visibility
  =========================== */
  const [showTopFilters, setShowTopFilters] = useState(true);
  const [showFilFilters, setShowFilFilters] = useState(true);
  const [showItemMaster, setShowItemMaster] = useState(false);
  const [showMainGrid, setShowMainGrid] = useState(false);

  /* ===========================
     data tables
  =========================== */
  const [itemMasterRows, setItemMasterRows] = useState([]);
  const [itemSelected, setItemSelected] = useState({});
  const [requestId, setRequestId] = useState("");
  const [mainRows, setMainRows] = useState([]);
  const [viewMasterRow, setViewMasterRow] = useState(null);
  const [viewMainIdx, setViewMainIdx] = useState(null);

  const didLoadRequestRef = useRef(false);
  const didAutoSearchRef = useRef(false);
  const isEditMode = !!requestId;

  const selectedItemIds = useMemo(
    () => Object.keys(itemSelected).filter((k) => itemSelected[k]),
    [itemSelected]
  );

  const allItemsSelected =
    itemMasterRows.length > 0 &&
    itemMasterRows.every((r) => {
      const id = String(extractItemIdFromMasterRow(r));
      return !!itemSelected[id];
    });

  /* ===========================
     styles
  =========================== */
  const inputClass = useMemo(
    () =>
      `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const filterPanelClass = useMemo(
    () => `p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`,
    [isDark]
  );

  /* ===========================
     handlers
  =========================== */
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const setTopField = useCallback((name, value) => {
    setTop((prev) => ({ ...prev, [name]: value }));
  }, []);

  const setFilField = useCallback((name, value) => {
    setFil((prev) => ({ ...prev, [name]: value }));
  }, []);

  const toggleItem = useCallback((key) => {
    setItemSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const toggleSelectAllItems = useCallback(() => {
    if (allItemsSelected) {
      setItemSelected({});
      return;
    }
    const next = {};
    itemMasterRows.forEach((r) => {
      const id = String(extractItemIdFromMasterRow(r));
      if (id) next[id] = true;
    });
    setItemSelected(next);
  }, [allItemsSelected, itemMasterRows]);

  const updateMainRow = useCallback((idx, patch) => {
    setMainRows((prev) => {
      const copy = [...prev];
      const row = { ...copy[idx], ...patch };
      row.Currentmonthfinalrequirement = calcFinal(row);
      copy[idx] = row;
      return copy;
    });
  }, []);

  const removeMainRow = useCallback((idx) => {
    setMainRows((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  /* ===========================
     init load
  =========================== */
  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);
      setInitLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: sessionBranchId,
          branchType,
          loginUserId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Init failed");
        return;
      }

      setBranches(normalizeOptionList(data?.branches, "Select"));
      setYears(normalizeYearList(data?.years));
      setMonths(normalizeOptionList(data?.months, "Select"));
      setItemCategories(ensureLubricationCategoryOptions(data?.itemCategories));
      setItemNames(normalizeOptionList(data?.itemNames, "Select"));
      setItemCodes(normalizeOptionList(data?.itemCodes, "Select"));

      if (Array.isArray(data?.warnings) && data.warnings.length) {
        console.warn("lubricationRequest/init warnings", data.warnings);
      }

      const d = data?.defaults || {};

      setTop((prev) => ({
        ...prev,
        branchId: String(d.branchId ?? prev.branchId ?? sessionBranchId),
        year: String(d.year ?? prev.year ?? "Select"),
        month: String(d.month ?? prev.month ?? "Select"),
      }));

      setFil({
        itemCategoryId: String(d.itemCategoryId ?? "37"),
        itemName: "Select",
        itemCode: "Select",
      });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to load init"
      );
    } finally {
      setInitLoading(false);
      setPageLoading(false);
    }
  }, [sessionBranchId, branchType, loginUserId, commonHeaders]);

  /* ===========================
     refresh filtration options
  =========================== */
  const refreshFiltrationOptions = useCallback(async () => {
    const branchId = toInt(top.branchId);
    if (!branchId) return;
    if (isSelect(top.month) || isSelect(top.year)) return;

    try {
      const { data } = await axios.get(`${API_BASE}/filtration/options`, {
        params: {
          branchId,
          month: top.month,
          year: top.year,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to refresh options");
      }

      setItemNames(normalizeOptionList(data?.itemNames, "Select"));
      setItemCodes(normalizeOptionList(data?.itemCodes, "Select"));

      setFil((prev) => ({
        ...prev,
        itemName: "Select",
        itemCode: "Select",
      }));
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err.message ||
          "Failed to refresh filters"
      );
    }
  }, [top.branchId, top.month, top.year, commonHeaders]);

  /* ===========================
     load existing request details
  =========================== */
  const loadRequestDetails = useCallback(
    async (reqId) => {
      if (!reqId) return;

      try {
        setMainLoading(true);

        const { data } = await axios.get(`${API_BASE}/request/details`, {
          params: { requestId: reqId },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load request details");
        }

        const rows = toList(data?.rows)
          .map(normalizeMainRow)
          .map((r) => ({ ...r, Currentmonthfinalrequirement: calcFinal(r) }));

        const header = data?.header || {};
        if (header.fld_branchid || header.fld_month || header.fld_year) {
          setTop((prev) => ({
            ...prev,
            branchId: String(header.fld_branchid ?? prev.branchId),
            month: String(header.fld_month ?? prev.month),
            year: String(header.fld_year ?? prev.year),
          }));
        }

        setRequestId(String(reqId));
        setMainRows(rows);
        setShowMainGrid(true);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message ||
            err.message ||
            "Failed to load request details"
        );
      } finally {
        setMainLoading(false);
      }
    },
    [commonHeaders]
  );

  /* ===========================
     top search
  =========================== */
  const handleTopSearch = useCallback(async () => {
    const branchId = toInt(top.branchId);
    if (!branchId) return toast.warning("Select Branch");
    if (isSelect(top.month)) return toast.warning("Select Month");
    if (isSelect(top.year)) return toast.warning("Select Year");

    try {
      setMainLoading(true);

      const { data } = await axios.get(`${API_BASE}/search/request`, {
        params: {
          branchId,
          month: top.month,
          year: top.year,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Search failed");
      }

      if (data?.alreadyCreated) {
        const reqId = String(data?.requestId || "");
        toast.info(data?.message || "Already request created");
        if (reqId) {
          await loadRequestDetails(reqId);
        } else {
          setMainRows([]);
          setShowMainGrid(true);
        }
        return;
      }

      const rows = toList(data?.rows)
        .map(normalizeMainRow)
        .map((r) => ({ ...r, Currentmonthfinalrequirement: calcFinal(r) }));

      setRequestId("");
      setMainRows(rows);
      setShowMainGrid(true);

      if (!rows.length) {
        toast.info(
          "No lubrication stock lines for this branch/month. Use Filteration to add items."
        );
      } else {
        toast.success("Loaded request data");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Top search failed"
      );
    } finally {
      setMainLoading(false);
    }
  }, [top.branchId, top.month, top.year, commonHeaders, loadRequestDetails]);

  /* ===========================
     filtration search
  =========================== */
  const handleFiltrationSearch = useCallback(async () => {
    const branchId = toInt(top.branchId);
    if (!branchId) return toast.warning("Select Branch");
    if (isSelect(top.month)) return toast.warning("Select Month");
    if (isSelect(top.year)) return toast.warning("Select Year");

    try {
      setItemMasterLoading(true);

      const { data } = await axios.get(`${API_BASE}/filtration/search`, {
        params: {
          branchId,
          itemCategoryId: isSelect(fil.itemCategoryId)
            ? "37"
            : fil.itemCategoryId,
          itemCode: fil.itemCode || "Select",
          itemName: fil.itemName || "Select",
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Filtration search failed");
      }

      setItemMasterRows(toList(data?.rows));
      setItemSelected({});
      setShowItemMaster(true);
      toast.success("Items loaded");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err.message ||
          "Filtration search failed"
      );
    } finally {
      setItemMasterLoading(false);
    }
  }, [top.branchId, top.month, top.year, fil, commonHeaders]);

  const handleClearFiltration = useCallback(() => {
    setFil((prev) => ({
      ...prev,
      itemName: "Select",
      itemCode: "Select",
    }));
    setItemMasterRows([]);
    setItemSelected({});
    setShowItemMaster(false);
  }, []);

  /* ===========================
     add selected items
  =========================== */
  const handleAdd = useCallback(async () => {
    const branchId = toInt(top.branchId);
    const selectedKeys = Object.keys(itemSelected).filter((k) => itemSelected[k]);

    if (!selectedKeys.length) {
      return toast.info("Select at least one item");
    }

    try {
      setMainLoading(true);

      const requests = selectedKeys.map((itemId) =>
        axios.get(`${API_BASE}/item/detail`, {
          params: {
            branchId,
            month: top.month,
            year: top.year,
            itemId,
          },
          headers: commonHeaders,
        })
      );

      const results = await Promise.all(requests);

      const newRows = results
        .map((r) => r.data?.row)
        .filter(Boolean)
        .map(normalizeMainRow)
        .map((r) => ({ ...r, Currentmonthfinalrequirement: calcFinal(r) }));

      setMainRows((prev) =>
        uniqBy([...prev, ...newRows], (x) => String(x.fld_itemid))
      );

      setShowMainGrid(true);
      toast.success(`Added ${newRows.length} item(s)`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Add failed");
    } finally {
      setMainLoading(false);
    }
  }, [top.branchId, top.month, top.year, itemSelected, commonHeaders]);

  /* ===========================
     submit
  =========================== */
  const handleSubmit = useCallback(async () => {
    const branchId = toInt(top.branchId);

    if (!branchId) return toast.warning("Select Branch");
    if (isSelect(top.month)) return toast.warning("Select Month");
    if (isSelect(top.year)) return toast.warning("Select Year");
    if (!mainRows.length) return toast.info("No Records Found!");

    try {
      setSubmitLoading(true);

      const payload = {
        requestId: requestId || "",
        branchId,
        month: top.month,
        year: top.year,
        loginUserId,
        items: mainRows.map((r) => ({
          itemId: r.fld_itemid,
          uomId: r.fld_uomid,
          openingqty: r.openingqty,
          received: r.received,
          totalpreviousmonth: r.totalpreviousmonth,
          consumptionpreviousmonth: r.consumptionpreviousmonth,
          closingsock: r.closingsock,
          totalconsumptionpreviousmont:
            r.totalconsumptionpreviousmont ?? r.closingsock,
          currentRequirement: r.Currentmonthrequirement,
          finalRequirement: r.Currentmonthfinalrequirement,
          remarks: r.Remarks,
        })),
      };

      const { data } = await axios.post(`${API_BASE}/submit`, payload, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Submit failed");
      }

      setRequestId(String(data?.requestId || ""));
      toast.success(
        data?.message || "Lubrication Request Details Saved Successfully"
      );

      setTimeout(() => {
        navigate("/vehicle/LubricationRequestListing");
      }, 700);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Submit error"
      );
    } finally {
      setSubmitLoading(false);
    }
  }, [
    top.branchId,
    top.month,
    top.year,
    mainRows,
    requestId,
    loginUserId,
    commonHeaders,
    navigate,
  ]);

  /* ===========================
     refresh
  =========================== */
  const handleRefresh = useCallback(async () => {
    await loadInit();

    if (requestId) {
      await loadRequestDetails(requestId);
    }
  }, [loadInit, requestId, loadRequestDetails]);

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    if (initLoading) return;
    refreshFiltrationOptions();
  }, [initLoading, refreshFiltrationOptions]);

  useEffect(() => {
    if (initLoading) return;
    if (!incomingRequestId) return;
    if (didLoadRequestRef.current) return;

    didLoadRequestRef.current = true;
    didAutoSearchRef.current = true;

    const rowMonth =
      incomingRow?.month || incomingRow?.Month || incomingRow?.fld_month;
    const rowYear =
      incomingRow?.year || incomingRow?.Year || incomingRow?.fld_year;
    const rowBranch =
      incomingRow?.branchId ||
      incomingRow?.fld_branchid ||
      incomingRow?.branch;

    if (rowMonth || rowYear || rowBranch) {
      setTop((prev) => ({
        ...prev,
        ...(rowBranch ? { branchId: String(rowBranch) } : {}),
        ...(rowMonth ? { month: String(rowMonth) } : {}),
        ...(rowYear ? { year: String(rowYear) } : {}),
      }));
    }

    loadRequestDetails(incomingRequestId);
  }, [initLoading, incomingRequestId, incomingRow, loadRequestDetails]);

  useEffect(() => {
    if (initLoading) return;
    if (incomingRequestId) return;
    if (didAutoSearchRef.current) return;
    if (!toInt(top.branchId)) return;
    if (isSelect(top.month) || isSelect(top.year)) return;

    didAutoSearchRef.current = true;
    handleTopSearch();
  }, [
    initLoading,
    incomingRequestId,
    top.branchId,
    top.month,
    top.year,
    handleTopSearch,
  ]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">LUBRICATION REQUEST</span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Lubrication Request
              </h2>
              <p
                className={`text-sm mt-1 ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Branch ID: {sessionBranchId || "-"} • User ID:{" "}
                {loginUserId || "-"}
                {requestId ? ` • Request ID: ${requestId}` : ""} • Mode:{" "}
                {isEditMode ? "EDIT" : "ENTRY"} • Rows: {mainRows.length}
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleSubmit}
                disabled={isBusy || !mainRows.length}
                type="button"
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                  isBusy || !mainRows.length
                    ? "opacity-60 cursor-not-allowed"
                    : ""
                }`}
              >
                <FaSave />
                {submitLoading ? "Submitting..." : "Submit"}
              </button>

              <button
                onClick={() => setShowTopFilters((p) => !p)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {showTopFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showTopFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {pageLoading || initLoading ? (
            <div
              className={`p-16 text-center ${
                isDark ? "bg-gray-800" : "bg-gray-50"
              }`}
            >
              <div
                className={`text-lg font-medium ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Loading...
              </div>
            </div>
          ) : (
            <>
              {/* ==================== SEARCH CRITERIA ==================== */}
              {showTopFilters && (
                <div className={filterPanelClass}>
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <h3
                      className={`text-lg font-semibold ${
                        isDark ? "text-white" : "text-blue-900"
                      }`}
                    >
                      Search Criteria
                    </h3>

                    <button
                      onClick={handleTopSearch}
                      type="button"
                      disabled={mainLoading || initLoading || submitLoading}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                        isBusy ? "opacity-60 cursor-not-allowed" : ""
                      } ${buttonClass} ${hoverClass} text-white`}
                    >
                      <FaSearch />
                      {mainLoading ? "Searching..." : "Search"}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className={labelClass}>Branch</label>
                      <select
                        className={inputClass}
                        value={top.branchId}
                        onChange={(e) =>
                          setTopField("branchId", e.target.value)
                        }
                        disabled={branchType !== "HO" || isEditMode || isBusy}
                      >
                        {branches.map((b, idx) => (
                          <option key={`${b.value}-${idx}`} value={b.value}>
                            {b.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Year</label>
                      <select
                        className={inputClass}
                        value={top.year}
                        onChange={(e) => setTopField("year", e.target.value)}
                        disabled={isEditMode || isBusy}
                      >
                        {years.map((y, idx) => (
                          <option key={`${y}-${idx}`} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Month</label>
                      <select
                        className={inputClass}
                        value={top.month}
                        onChange={(e) => setTopField("month", e.target.value)}
                        disabled={isEditMode || isBusy}
                      >
                        <option value="Select">Select</option>
                        {months
                          .filter((m) => m.value !== "Select")
                          .map((m, idx) => (
                            <option key={`${m.value}-${idx}`} value={m.label}>
                              {m.label}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* ==================== ITEM FILTERS ==================== */}
              <div className={filterPanelClass}>
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <h3
                    className={`text-lg font-semibold ${
                      isDark ? "text-white" : "text-blue-900"
                    }`}
                  >
                    Item Filters
                  </h3>

                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => setShowFilFilters((p) => !p)}
                      type="button"
                      className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                        isDark
                          ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-white"
                      }`}
                    >
                      {showFilFilters ? "Hide" : "Show"}
                    </button>

                    <button
                      onClick={handleClearFiltration}
                      type="button"
                      disabled={isBusy}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                        isDark
                          ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-white"
                      } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      Clear
                    </button>

                    <button
                      onClick={handleFiltrationSearch}
                      type="button"
                      disabled={
                        itemMasterLoading ||
                        initLoading ||
                        mainLoading ||
                        submitLoading
                      }
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                        isBusy ? "opacity-60 cursor-not-allowed" : ""
                      } ${buttonClass} ${hoverClass} text-white`}
                    >
                      <FaSearch />
                      {itemMasterLoading ? "Searching..." : "Search"}
                    </button>
                  </div>
                </div>

                {showFilFilters && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className={labelClass}>Item Category</label>
                      <select
                        className={inputClass}
                        value={fil.itemCategoryId}
                        onChange={(e) =>
                          setFilField("itemCategoryId", e.target.value)
                        }
                        disabled={true}
                      >
                        {itemCategories.map((c, idx) => (
                          <option key={`${c.value}-${idx}`} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Item Name</label>
                      <select
                        className={inputClass}
                        value={fil.itemName}
                        onChange={(e) =>
                          setFilField("itemName", e.target.value)
                        }
                        disabled={isBusy}
                      >
                        {itemNames.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>
                            {x.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Item Code</label>
                      <select
                        className={inputClass}
                        value={fil.itemCode}
                        onChange={(e) =>
                          setFilField("itemCode", e.target.value)
                        }
                        disabled={isBusy}
                      >
                        {itemCodes.map((x, idx) => (
                          <option key={`${x.value}-${idx}`} value={x.value}>
                            {x.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* ==================== ITEM MASTER TABLE ==================== */}
              {showItemMaster && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                    <h3
                      className={`text-lg font-semibold ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Items ({itemMasterRows.length})
                    </h3>

                    <button
                      onClick={handleAdd}
                      type="button"
                      disabled={isBusy || selectedItemIds.length === 0}
                      className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                        isBusy || selectedItemIds.length === 0
                          ? "opacity-60 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <FaPlus />
                      Add
                    </button>
                  </div>

                  <div className="overflow-hidden rounded-lg border shadow-lg">
                    {itemMasterLoading ? (
                      <div
                        className={`p-16 text-center ${
                          isDark ? "bg-gray-800" : "bg-gray-50"
                        }`}
                      >
                        <div
                          className={`text-lg font-medium ${
                            isDark ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          Loading...
                        </div>
                      </div>
                    ) : itemMasterRows.length === 0 ? (
                      <div
                        className={`p-16 text-center ${
                          isDark ? "bg-gray-800" : "bg-gray-50"
                        }`}
                      >
                        <div
                          className={`text-lg font-medium ${
                            isDark ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          No Records found
                        </div>
                      </div>
                    ) : (
                      <table
                        className={`w-full table-fixed ${
                          isDark ? "bg-gray-800" : "bg-white"
                        }`}
                      >
                        <thead className={`${tableHeaderClass} text-white`}>
                          <tr>
                            <th className="px-4 py-4 text-left text-sm font-semibold w-16">
                              S.No
                            </th>
                            <th className="px-4 py-4 text-left text-sm font-semibold">
                              Item Code
                            </th>
                            <th className="px-4 py-4 text-left text-sm font-semibold">
                              Item Name
                            </th>
                            <th className="px-4 py-4 text-center text-sm font-semibold w-16">
                              View
                            </th>
                            <th className="px-4 py-4 text-center text-sm font-semibold w-16">
                              <input
                                type="checkbox"
                                checked={allItemsSelected}
                                onChange={toggleSelectAllItems}
                                className="w-4 h-4 cursor-pointer"
                                title="Select All"
                                disabled={isBusy}
                              />
                            </th>
                          </tr>
                        </thead>

                        <tbody
                          className={`${
                            isDark ? "divide-gray-700" : "divide-gray-200"
                          } divide-y`}
                        >
                          {itemMasterRows.map((r, idx) => {
                            const itemId = String(
                              extractItemIdFromMasterRow(r)
                            );
                            const d = extractDisplayFromMasterRow(r);

                            return (
                              <tr
                                key={`${itemId}-${idx}`}
                                className={`${
                                  idx % 2 === 0
                                    ? isDark
                                      ? "bg-gray-800"
                                      : "bg-white"
                                    : isDark
                                    ? "bg-gray-750"
                                    : "bg-gray-50"
                                } transition-colors ${
                                  isDark
                                    ? "hover:bg-gray-700"
                                    : "hover:bg-blue-50"
                                }`}
                              >
                                <td
                                  className={`px-4 py-3 text-sm ${
                                    isDark ? "text-gray-300" : "text-gray-900"
                                  }`}
                                >
                                  {idx + 1}
                                </td>
                                <td
                                  className={`px-4 py-3 text-sm font-medium truncate ${
                                    isDark ? "text-blue-400" : "text-blue-600"
                                  }`}
                                >
                                  {d.itemCode || itemId}
                                </td>
                                <td
                                  className={`px-4 py-3 text-sm font-medium truncate ${
                                    isDark ? "text-white" : "text-gray-900"
                                  }`}
                                >
                                  {d.itemName}
                                </td>
                                <td className="px-4 py-4 text-center">
                                  <button
                                    type="button"
                                    onClick={() => setViewMasterRow({ ...r, ...d, itemId })}
                                    className="text-blue-500 hover:text-blue-600 text-lg"
                                    title="View"
                                  >
                                    <FaEye />
                                  </button>
                                </td>
                                <td className="px-4 py-4 text-center">
                                  <input
                                    type="checkbox"
                                    checked={!!itemSelected[itemId]}
                                    onChange={() => toggleItem(itemId)}
                                    disabled={isBusy}
                                    className="w-4 h-4 cursor-pointer"
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}

              {/* ==================== REQUEST GRID ==================== */}
              {showMainGrid && (
                <div>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                    <h3
                      className={`text-lg font-semibold ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Request Grid ({mainRows.length})
                    </h3>
                  </div>

                  <div className="overflow-hidden rounded-lg border shadow-lg">
                    {mainLoading ? (
                      <div
                        className={`p-16 text-center ${
                          isDark ? "bg-gray-800" : "bg-gray-50"
                        }`}
                      >
                        <div
                          className={`text-lg font-medium ${
                            isDark ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          Loading...
                        </div>
                      </div>
                    ) : mainRows.length === 0 ? (
                      <div
                        className={`p-16 text-center ${
                          isDark ? "bg-gray-800" : "bg-gray-50"
                        }`}
                      >
                        <div
                          className={`text-lg font-medium ${
                            isDark ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          No Records found
                        </div>
                      </div>
                    ) : (
                      <>
                        <table
                          className={`w-full table-fixed ${
                            isDark ? "bg-gray-800" : "bg-white"
                          }`}
                        >
                          <thead className={`${tableHeaderClass} text-white`}>
                            <tr>
                              <th className="px-4 py-4 text-left text-sm font-semibold">
                                Item
                              </th>
                              <th className="px-4 py-4 text-left text-sm font-semibold w-36">
                                Current Req
                              </th>
                              <th className="px-4 py-4 text-left text-sm font-semibold w-28">
                                Final Req
                              </th>
                              <th className="px-4 py-4 text-center text-sm font-semibold w-16">
                                View
                              </th>
                              <th className="px-4 py-4 text-center text-sm font-semibold w-16">
                                Action
                              </th>
                            </tr>
                          </thead>

                          <tbody
                            className={`${
                              isDark ? "divide-gray-700" : "divide-gray-200"
                            } divide-y`}
                          >
                            {mainRows.map((r, idx) => (
                              <tr
                                key={`${r.fld_itemid}-${idx}`}
                                className={`${
                                  idx % 2 === 0
                                    ? isDark
                                      ? "bg-gray-800"
                                      : "bg-white"
                                    : isDark
                                    ? "bg-gray-750"
                                    : "bg-gray-50"
                                } transition-colors ${
                                  isDark
                                    ? "hover:bg-gray-700"
                                    : "hover:bg-blue-50"
                                }`}
                              >
                                <td className="px-4 py-3 text-sm">
                                  <div
                                    className={`font-medium truncate ${
                                      isDark ? "text-white" : "text-gray-900"
                                    }`}
                                  >
                                    {r.fld_itemname}
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    className={`${inputClass} text-right`}
                                    value={r.Currentmonthrequirement}
                                    onChange={(e) =>
                                      updateMainRow(idx, {
                                        Currentmonthrequirement: toNum(
                                          e.target.value
                                        ),
                                      })
                                    }
                                    disabled={submitLoading}
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    className={`${inputClass} text-right opacity-70`}
                                    value={r.Currentmonthfinalrequirement}
                                    readOnly
                                  />
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <button
                                    type="button"
                                    onClick={() => setViewMainIdx(idx)}
                                    className="text-blue-500 hover:text-blue-600 text-lg"
                                    title="View"
                                  >
                                    <FaEye />
                                  </button>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <button
                                    onClick={() => removeMainRow(idx)}
                                    type="button"
                                    disabled={isBusy}
                                    className={`text-red-500 hover:text-red-600 text-lg transition ${
                                      isBusy
                                        ? "opacity-50 cursor-not-allowed"
                                        : ""
                                    }`}
                                    title="Remove"
                                  >
                                    <FaTrash className="inline w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        <div className="p-4">
                          <button
                            onClick={handleSubmit}
                            disabled={isBusy || !mainRows.length}
                            type="button"
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                              isBusy || !mainRows.length
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            } ${buttonClass} ${hoverClass} text-white`}
                          >
                            <FaSave />
                            {submitLoading ? "Submitting..." : "Submit"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}


          {(viewMasterRow || viewMainIdx !== null) && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
              onClick={() => {
                setViewMasterRow(null);
                setViewMainIdx(null);
              }}
            >
              <div
                className={`w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl shadow-2xl border ${
                  isDark
                    ? "bg-gray-900 border-gray-700 text-white"
                    : "bg-white border-gray-200 text-gray-900"
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                  <h3 className="text-xl font-bold">
                    {viewMasterRow ? "Item Details" : "Request Line Details"}
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setViewMasterRow(null);
                      setViewMainIdx(null);
                    }}
                    className={`p-2 rounded-lg ${isDark ? "hover:bg-gray-800" : "hover:bg-gray-100"}`}
                  >
                    <FaTimes />
                  </button>
                </div>

                {viewMasterRow && (
                  <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      ["Item Code", viewMasterRow.itemCode || viewMasterRow.itemId],
                      ["Item Name", viewMasterRow.itemName],
                      ["Part No", viewMasterRow.partNo],
                      ["UOM", viewMasterRow.uom],
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className={`p-3 rounded-lg border ${
                          isDark ? "bg-gray-800/60 border-gray-700" : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div className={`text-xs font-semibold uppercase mb-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                          {label}
                        </div>
                        <div className="text-sm font-medium break-words">{value || "-"}</div>
                      </div>
                    ))}
                  </div>
                )}

                {viewMainIdx !== null && mainRows[viewMainIdx] && (
                  <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      ["Item", mainRows[viewMainIdx].fld_itemname],
                      ["Item ID", mainRows[viewMainIdx].fld_itemid],
                      ["UOM", mainRows[viewMainIdx].fld_uomname],
                      ["Opening", toNum(mainRows[viewMainIdx].openingqty).toFixed(3)],
                      ["Received", toNum(mainRows[viewMainIdx].received).toFixed(3)],
                      ["Total", toNum(mainRows[viewMainIdx].totalpreviousmonth).toFixed(3)],
                      ["Consumption", toNum(mainRows[viewMainIdx].consumptionpreviousmonth).toFixed(3)],
                      ["Consumption Stock", toNum(mainRows[viewMainIdx].closingsock).toFixed(3)],
                      ["Final Req", toNum(mainRows[viewMainIdx].Currentmonthfinalrequirement).toFixed(3)],
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className={`p-3 rounded-lg border ${
                          isDark ? "bg-gray-800/60 border-gray-700" : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div className={`text-xs font-semibold uppercase mb-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                          {label}
                        </div>
                        <div className="text-sm font-medium break-words">{value || "-"}</div>
                      </div>
                    ))}
                    <div className="md:col-span-2">
                      <label className={labelClass}>Current Requirement</label>
                      <input
                        className={inputClass}
                        value={mainRows[viewMainIdx].Currentmonthrequirement}
                        onChange={(e) =>
                          updateMainRow(viewMainIdx, {
                            Currentmonthrequirement: toNum(e.target.value),
                          })
                        }
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelClass}>Remarks</label>
                      <input
                        className={inputClass}
                        value={mainRows[viewMainIdx].Remarks}
                        onChange={(e) =>
                          updateMainRow(viewMainIdx, {
                            Remarks: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}